import { Section, SoundPackage, CuratedSet } from '@bpm-web-app/create-api-sdk';
import { useCreateGenres, useGetFeaturedSoundPackages, useGetNewestSoundPackages, useGetSections, useGetSuggestedSoundPackages, useTrendingGetLabels } from '@bpm-web-app/swr-hooks';
import { convertSoundPackageToCarouselProps, createAppRoutes, soundPackageToCreatePlayable, DEFAULT_PACKS_LIMIT, curatedPackToCreatePlayable, useUserSettings } from '@bpm-web-app/utils';
import { useCallback, useMemo, useState } from 'react';
import { PromotionsMessagingModal } from '@bpm-web-app/components';
import { ClickableGenres } from '../../clickable-genres/clickable-genres';
import { CreateCardCarousel } from '../../create-card-carousel/create-card-carousel';
import { useCreatePlayer } from '../../../../../utils/src/lib/create-player.context';
import Banner from '../../shared/banner/banner';
import GenresCarousel from '../../genres-carousel/genres-carousel';
import TrendingBrowseCreate from '../../trending/trending-browse-create';
import { GenreSectionComponent } from './create-browse-genre-section-component';
import { NewsCarousel } from '../../shared/news-carousel/news-carousel';
import { AnonymousUserBanner, MessageBanner } from '../../shared';

export function CreateBrowse() {
    const { data: sectionsData, isLoading: isLoadingSectionData } = useGetSections('browse');
    const { data: newestSoundsData, isLoading: isLoadingNewestSoundsData } = useGetNewestSoundPackages({ limit: DEFAULT_PACKS_LIMIT });
    const newestSoundsCarouselData = useMemo(() => newestSoundsData?.data.map((data) => convertSoundPackageToCarouselProps(data)), [newestSoundsData?.data]);

    const { data: featuredSoundsData, isLoading: isLoadingFeaturedSoundsData } = useGetFeaturedSoundPackages({ limit: DEFAULT_PACKS_LIMIT });

    const featuredCarouselData = useMemo(
        () =>
            featuredSoundsData?.data.map((data) =>
                convertSoundPackageToCarouselProps(
                    data,
                    <div className="card__darker-padded-description">
                        <ClickableGenres genres={data.Genre} />
                    </div>
                )
            ),
        [featuredSoundsData?.data]
    );

    const { data: labels, isLoading: isLoadingLabelData } = useTrendingGetLabels();
    const labelCarouselData = useMemo(
        () =>
            labels?.map((label) => ({
                id: label.id,
                title: label.name,
                coverUrl: label.logo_url,
                slug: label.name,
            })),
        [labels]
    );

    const { data: suggestedSoundPacks, isLoading: isLoadingSuggestedData } = useGetSuggestedSoundPackages();
    const suggestedCarouselData = useMemo(
        () => suggestedSoundPacks?.data.map((data) => convertSoundPackageToCarouselProps(data, <ClickableGenres genres={data.Genre} />)),
        [suggestedSoundPacks?.data]
    );

    const { genres, isLoading: isLoadingGenres } = useCreateGenres();

    const sectionItemsConverter = useCallback((items: typeof sectionsData['data'][0]['items']) => items?.map((pack) => convertSoundPackageToCarouselProps(pack)), []);

    const { playDemo } = useCreatePlayer();

    const [isBannerDataLoaded, setIsBannerDataLoaded] = useState(false);
    const [isTrendingDataLoaded, setIsTrendingDataLoaded] = useState(false);
    const [isNewsLoaded, setIsNewsLoaded] = useState(false);
    const { isAnonymous } = useUserSettings();

    const isEverythingLoaded = useMemo(() => {
        if (
            !isLoadingSectionData &&
            !isLoadingNewestSoundsData &&
            !isLoadingFeaturedSoundsData &&
            !isLoadingLabelData &&
            !isLoadingSuggestedData &&
            !isLoadingGenres &&
            isBannerDataLoaded &&
            isTrendingDataLoaded &&
            isNewsLoaded
        ) return true;
        return false;
    }, [
        isBannerDataLoaded,
        isLoadingFeaturedSoundsData,
        isLoadingGenres,
        isLoadingLabelData,
        isLoadingNewestSoundsData,
        isLoadingSectionData,
        isLoadingSuggestedData,
        isNewsLoaded,
        isTrendingDataLoaded
    ]);

    return (
        <div>
            {isAnonymous ? <MessageBanner type="info" title="You’re currently previewing BPM Create." viewPlanRoute /> : null}
            <Banner platform="create" isDataLoaded={isEverythingLoaded} setIsDataLoaded={setIsBannerDataLoaded} />
            <PromotionsMessagingModal />
            {sectionsData?.data?.map((section) => {
                if (section.type === Section.TypeEnum.Virtual) {
                    switch (section.name) {
                        case 'Newest Packs':
                            return (
                                <CreateCardCarousel
                                    contentType="pack"
                                    key={section.id}
                                    items={newestSoundsCarouselData}
                                    cardSize="small"
                                    carouselMorePath={createAppRoutes.newestPacks}
                                    isLoading={!isEverythingLoaded}
                                    carouselTitle="Newest Sound Packs"
                                    onCardPlay={(id) => playDemo(soundPackageToCreatePlayable(newestSoundsData.data.find((item) => item.id === id)))}
                                    itemBasePath={createAppRoutes.packs}
                                />
                            );
                        case 'Featured Packs': {
                            return (
                                <CreateCardCarousel
                                    contentType="pack"
                                    key={section.id}
                                    items={featuredCarouselData}
                                    cardSize="small"
                                    carouselMorePath={createAppRoutes.featured}
                                    isLoading={!isEverythingLoaded}
                                    carouselTitle="Featured"
                                    onCardPlay={(id) => playDemo(soundPackageToCreatePlayable(featuredSoundsData.data.find((item) => item.id === id)))}
                                    itemBasePath={createAppRoutes.packs}
                                    linesWidthArrayGhost={[80, 100]}
                                />
                            );
                        }
                        case 'Trending Packs':
                            return <TrendingBrowseCreate key={section.id} isDataLoaded={isEverythingLoaded} setIsDataLoaded={setIsTrendingDataLoaded} />;
                        case 'Hip Hop':
                            return <GenreSectionComponent slug="Hip Hop & R&B" sectionName={section.name} sectionId={section.id} key={section.id} isLoading={!isEverythingLoaded} />;
                        case 'EDM':
                            return <GenreSectionComponent slug="EDM / Radio Pop" sectionName={section.name} sectionId={section.id} key={section.id} isLoading={!isEverythingLoaded} />;
                        case 'Global':
                            return <GenreSectionComponent slug="Global" sectionName={section.name} sectionId={section.id} key={section.id} isLoading={!isEverythingLoaded} />;
                        case 'Genres':
                            return <GenresCarousel genres={genres} key={section.id} isLoading={!isEverythingLoaded} />;
                        case 'For You': {
                            return (
                                <CreateCardCarousel
                                    contentType="pack"
                                    key={section.id}
                                    items={suggestedCarouselData}
                                    cardSize="small"
                                    carouselMorePath={createAppRoutes.suggestions}
                                    isLoading={!isEverythingLoaded}
                                    carouselTitle="For You"
                                    onCardPlay={(id) => playDemo(soundPackageToCreatePlayable(suggestedSoundPacks.data.find((item) => item.id === id)))}
                                    itemBasePath={createAppRoutes.packs}
                                />
                            );
                        }
                        default:
                            return null;
                    }
                } else {
                    // eslint-disable-next-line no-underscore-dangle
                    const type = (section.items?.[0] as any).__typename;
                    return (
                        <CreateCardCarousel
                            contentType={type === 'Curated' ? 'curated-set' : 'pack'}
                            key={section.id}
                            items={sectionItemsConverter(section.items)}
                            cardSize="small"
                            carouselTitle={section.name}
                            isLoading={!isEverythingLoaded}
                            onCardPlay={(id) => type === 'Curated' ? playDemo(curatedPackToCreatePlayable((section.items as CuratedSet[]).find((item) => item.id === id))) : playDemo(soundPackageToCreatePlayable((section.items as SoundPackage[]).find((item) => item.id === id)))}
                            carouselMorePath={createAppRoutes.section(section.name, 'browse', type === 'Curated' ? 'curated' : 'soundPackage')}
                            itemBasePath={type === 'Curated' ? createAppRoutes.curated : createAppRoutes.packs}
                        />
                    );
                }
            })}
            <AnonymousUserBanner isCreate />

            <CreateCardCarousel
                contentType="label"
                items={labelCarouselData}
                cardSize="large"
                carouselMorePath={createAppRoutes.labels}
                isLoading={!isEverythingLoaded}
                carouselTitle="Trending Labels"
                itemBasePath={createAppRoutes.labels}
            />
            {/* eslint-disable-next-line @typescript-eslint/dot-notation */}
            <NewsCarousel newsProviderId={`${process.env['NEXT_PUBLIC_MAIN_BPM_NEWS_PROVIDER_ID']}`} isCreate isDataLoaded={isEverythingLoaded} setIsDataLoaded={setIsNewsLoaded} />
        </div>
    );
}
